import { 
  signInWithPopup,
  GoogleAuthProvider, 
  signOut as firebaseSignOut 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const googleProvider = new GoogleAuthProvider();

export const authService = {
  /**
   * Sign in with Google popup
   * Opens a popup window for Google OAuth
   * @returns {Promise<{user: Object, userData: Object}>}
   */
  signInWithGoogle: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const userData = await authService.getUserData(user.email);
      
      if (!userData || !userData.allowed) {
        await firebaseSignOut(auth);
        throw new Error('Access denied. Your email is not authorized to access this application.');
      }
      
      return { user, userData };
    } catch (error) {
      console.error('Error with Google sign-in:', error);
      throw error;
    }
  },

  /**
   * Sign out the current user
   * @returns {Promise<void>}
   */
  signOut: async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  },

  /**
   * Get user data from Firestore by email
   * @param {string} email - User's email address
   * @returns {Promise<Object|null>} User data or null if not found
   */
  getUserData: async (email) => {
    try {
      if (!email) return null;
      
      // Use email as document ID (sanitized)
      const sanitizedEmail = email.toLowerCase();
      const userDoc = await getDoc(doc(db, 'users', sanitizedEmail));
      
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  },

  /**
   * Get the current Firebase auth instance
   * @returns {Auth} Firebase auth instance
   */
  getAuth: () => auth
};
